// Updated Login.jsx with Remember Me functionality
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styles from './Login.module.css';
import { Mail, Lock, User, AlertCircle, CheckCircle, RefreshCw, ArrowLeft, Loader2, CreditCard, Eye, EyeOff } from 'lucide-react';
import { loginUser, resendVerificationCode } from '../../Fetch/fetchUser';
import { getBuildingSubscription } from '../../Fetch/fetchSubscription';
import { enhancedTokenManager } from '../../utils/enhancedTokenManager'; // Updated import
import { debugStorage } from '../../utils/debugStorage'; // Debug utility
import { useLanguage } from '../../contexts/LanguageContext';
import { useTranslation } from '../../translations/translations';
import { useApiWithErrorHandling } from '../../hooks/useApiWithErrorHandling';

export default function Login({
    setIsLoggedIn,
    setUserData,
    onLoginSuccess,
    setCurrentPage,
    showBackButton = false,
    onBack
}) {
    const { language } = useLanguage();
    const { t } = useTranslation(language);
    const { executeWithErrorHandling } = useApiWithErrorHandling();
    
    const [formData, setFormData] = useState({
        email: '',
        password: '',
        rememberMe: false // Add remember me state
    });
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [showResendVerification, setShowResendVerification] = useState(false);
    const [resendEmail, setResendEmail] = useState('');
    const [isResending, setIsResending] = useState(false);
    const [resendSuccess, setResendSuccess] = useState('');
    const [showPassword, setShowPassword] = useState(false);

    // Subscription-related state
    const [subscriptionExpired, setSubscriptionExpired] = useState(false);
    const [expiredBuildingId, setExpiredBuildingId] = useState(null);

    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        // Check for verification messages from URL or location state
        const queryParams = new URLSearchParams(location.search);
        const verified = queryParams.get('verified');
        const message = queryParams.get('message');
        const errorMsg = queryParams.get('error');

        const state = location.state || {};

        const registrationSuccess = localStorage.getItem('registrationSuccess');
        if (registrationSuccess) {
            setSuccess(registrationSuccess);
            localStorage.removeItem('registrationSuccess');
        } else if (verified === 'true' || state.verificationStatus === 'success') {
            setSuccess(message || state.message || t('loginSuccessMessage'));
        } else if (state.message) {
            setSuccess(state.message);
        } else if (errorMsg || state.verificationStatus === 'error') {
            setError(errorMsg || state.message || t('loginFailedMessage'));
        }

        const pendingVerification = localStorage.getItem('pendingVerification');
        if (pendingVerification) {
            try {
                const { email } = JSON.parse(pendingVerification);
                if (email) {
                    setResendEmail(email);
                    setFormData(prev => ({ ...prev, email }));
                }
            } catch (e) {
            }
        }

        // Check if user had "remember me" enabled previously
        const wasRemembered = enhancedTokenManager.isRememberMeEnabled();
        if (wasRemembered) {
            setFormData(prev => ({ ...prev, rememberMe: true }));
            
            // Try to get the email from stored user data
            const storedUser = enhancedTokenManager.getUserData();
            if (storedUser?.email) {
                setFormData(prev => ({ ...prev, email: storedUser.email }));
            }
        }

        if (state.message) {
            const timer = setTimeout(() => {
                setSuccess('');
            }, 10000);
            return () => clearTimeout(timer);
        }
    }, [location]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prevState => ({
            ...prevState,
            [name]: type === 'checkbox' ? checked : value
        }));

        if (error) {
            setError('');
        }
        if (subscriptionExpired) {
            setSubscriptionExpired(false);
        }
    };

    // Helper function to check permission for specific building
    const hasLoginPermissionInBuilding = (building) => {
        return building.isManager === "כן" || building.isVaad === "כן";
    };

    // Check subscription status using CORRECT logic
    const checkSubscriptionStatus = async (buildingId) => {
        try {

            const apiResponse = await executeWithErrorHandling(
                () => getBuildingSubscription(buildingId)
            );

            if (!apiResponse) {
                return { isExpired: false, subscription: null };
            }

            // Handle wrapped response structure: { success: true, data: {...} }
            let subscriptionData;
            if (apiResponse.success && apiResponse.data) {
                subscriptionData = apiResponse.data;
            } else if (apiResponse.trialEndDate || apiResponse.nextPaymentDue) {
                // Direct subscription data (fallback)
                subscriptionData = apiResponse;
            } else {
                return { isExpired: false, subscription: apiResponse };
            }

            // CORRECT LOGIC: Check subscription status
            const currentDate = new Date();
            let isExpired = false;
            let expirationReason = '';

            // Check TrialEndDate
            let trialExpired = false;
            if (subscriptionData.trialEndDate) {
                const trialEndDate = new Date(subscriptionData.trialEndDate);
                
                // Check if trialEndDate is DateTime.MinValue (1/1/0001 in JavaScript is 1/1/1970 or invalid)
                const isMinValue = trialEndDate.getFullYear() <= 1970 || isNaN(trialEndDate.getTime());
                
                if (!isMinValue) {
                    trialExpired = currentDate > trialEndDate;
                } else {
                }
            }

            // Check NextPaymentDue
            let paymentExpired = false;
            if (subscriptionData.nextPaymentDue) {
                const nextPaymentDue = new Date(subscriptionData.nextPaymentDue);
                paymentExpired = currentDate > nextPaymentDue;
                
            }

            // CORRECT DETERMINATION LOGIC: isExpired = true ONLY when BOTH trial AND payment expired
            if (subscriptionData.trialEndDate && new Date(subscriptionData.trialEndDate).getFullYear() > 1970) {
                // Trial period exists and is not MinValue
                if (trialExpired && paymentExpired) {
                    isExpired = true;
                    expirationReason = 'Both trial period and payment period expired';
                } else if (!trialExpired) {
                    isExpired = false;
                    expirationReason = 'Trial period still active';
                } else if (trialExpired && !paymentExpired) {
                    isExpired = false;
                    expirationReason = 'Trial expired but payment period still active';
                }
            } else {
                // No trial or trial is MinValue - only check payment
                isExpired = paymentExpired;
                expirationReason = paymentExpired ? 'Payment period expired' : 'Payment period active';
            }


            return {
                isExpired,
                subscription: subscriptionData,
                method: 'correct-trial-and-payment-check',
                buildingId: buildingId,
                details: {
                    trialExpired,
                    paymentExpired,
                    reason: expirationReason
                }
            };

        } catch (error) {
            // If we can't check subscription, assume expired for safety
            return { 
                isExpired: true, 
                subscription: null, 
                error: error.message, 
                buildingId: buildingId 
            };
        }
    };

    // Check all buildings using CORRECT logic
    const checkAllBuildingsSubscription = async (buildings) => {
        try {

            const subscriptionPromises = buildings.map(building =>
                checkSubscriptionStatus(building.buildingId)
                    .then(result => ({
                        building,
                        subscription: result
                    }))
                    .catch(error => ({
                        building,
                        subscription: { isExpired: true, error: error.message, buildingId: building.buildingId }
                    }))
            );

            const results = await Promise.all(subscriptionPromises);

            // CORRECT: Buildings are paid if subscription is NOT expired
            const paidBuildings = results.filter(result => !result.subscription.isExpired);
            const unpaidBuildings = results.filter(result => result.subscription.isExpired);


            return {
                paidBuildings,
                unpaidBuildings,
                allResults: results,
                hasAnyPaidBuilding: paidBuildings.length > 0
            };
        } catch (error) {
            return {
                paidBuildings: [],
                unpaidBuildings: buildings.map(building => ({ building, subscription: { isExpired: true, error: 'Check failed' } })),
                allResults: [],
                hasAnyPaidBuilding: false
            };
        }
    };

    const handleGoToPayment = () => {

        navigate('/app/subscription-payment', {
            state: {
                buildingId: expiredBuildingId,
                reason: 'trial_expired',
                returnTo: '/app/dashboard'
            }
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setError('');
        setSuccess('');
        setShowResendVerification(false);
        setSubscriptionExpired(false);

        if (!formData.email.trim()) {
            setError(t('emailRequired'));
            setIsLoading(false);
            return;
        }

        if (!formData.password.trim()) {
            setError(t('passwordRequired'));
            setIsLoading(false);
            return;
        }

        try {

            // Step 1: Authenticate user
            const data = await executeWithErrorHandling(
                () => loginUser(formData)
            );

            // Step 2: Store token with remember me preference using enhanced token manager
            enhancedTokenManager.setToken(data.token, formData.rememberMe, data.refreshToken);
            
            // Debug: Check storage immediately after setting
            debugStorage.checkAllStorage();

            // Step 3: Check buildings and permissions
            const buildings = data.user.buildings || [];

            if (buildings.length === 0) {
                setError(t('userNotAssociatedWithBuilding'));
                setIsLoading(false);
                return;
            }

            const authorizedBuildings = buildings.filter(hasLoginPermissionInBuilding);

            if (authorizedBuildings.length === 0) {
                setError(t('noPermissionsToAccess'));
                setIsLoading(false);
                return;
            }

            // Step 4: Check subscription status for ALL buildings using CORRECT logic
            const subscriptionResults = await checkAllBuildingsSubscription(authorizedBuildings);

            // Store subscription info for later use in Layout
            localStorage.setItem('buildingSubscriptions', JSON.stringify(subscriptionResults.allResults));

            // Allow login if at least one building is paid
            if (!subscriptionResults.hasAnyPaidBuilding) {

                // Store user temporarily with remember me preference
                enhancedTokenManager.setUserData({
                    ...data.user,
                    selectedBuilding: authorizedBuildings[0] // Default to first building for payment
                }, formData.rememberMe);

                setSubscriptionExpired(true);
                setExpiredBuildingId(authorizedBuildings[0].buildingId);
                setError(t('trialExpiredAllBuildings'));
                setIsLoading(false);
                return;
            }

            // Step 5: Complete login with first paid building
            const firstPaidBuilding = subscriptionResults.paidBuildings[0].building;
            
            
            completeLogin(data.user, firstPaidBuilding);

        } catch (error) {
            enhancedTokenManager.clearAuth(); // Clear all auth data on failure

            // Don't handle network errors here - they're handled by the popup
            if (error.message !== 'NETWORK_ERROR') {
                if (error.requiresVerification) {
                    setError(error.message);
                    setShowResendVerification(true);
                    setResendEmail(formData.email);

                    localStorage.setItem('pendingVerification', JSON.stringify({
                        email: formData.email,
                        timestamp: Date.now()
                    }));
                } else {
                    const errorMessage = error.message || t('loginFailed');
                    setError(errorMessage);

                    if (errorMessage.includes('שגויים') || errorMessage.includes('שגוי')) {
                        setFormData(prev => ({ ...prev, password: '' }));
                    }
                }
            }
        } finally {
            setIsLoading(false);
        }
    };

    const completeLogin = (user, selectedBuildingData) => {

        const userData = {
            userId: user.userId,
            name: user.fullName,
            email: user.email,
            buildingId: selectedBuildingData.buildingId,
            buildingIds: user.buildings.map(b => b.buildingId),
            ismanager: selectedBuildingData.isManager,
            isvaad: selectedBuildingData.isVaad,
            istenant: selectedBuildingData.isTenant,
            currentBuilding: selectedBuildingData,
            profilePicture: user.profilePicture,
            profileImagePath: user.profileImagePath
        };

        // Store user data with remember me preference using enhanced token manager
        enhancedTokenManager.setUserData(userData, formData.rememberMe);
        
        // Debug: Check storage after setting user data
        debugStorage.checkAllStorage();

        localStorage.removeItem('pendingVerification');
        localStorage.removeItem('tempUser');

        setIsLoggedIn(true);
        setUserData(userData);

        if (onLoginSuccess) {
            onLoginSuccess(user.fullName, user.email);
        }

        navigate('/app/dashboard');
    };

    const handleResendVerification = async () => {
        if (!resendEmail) {
            setError(t('emailNotAvailable'));
            return;
        }

        setIsResending(true);
        setError('');
        setResendSuccess('');

        try {
            await executeWithErrorHandling(
                () => resendVerificationCode(resendEmail)
            );
            setResendSuccess(t('newVerificationCodeSent'));
            setShowResendVerification(false);
            
            // Auto-navigate to verification page after successful email send
            setTimeout(() => {
                handleGoToVerification();
            }, 1500); // Give user time to see the success message
            
        } catch (error) {
            if (error.message !== 'NETWORK_ERROR') {
                setError(error.message || t('errorSendingVerificationCode'));
            }
        } finally {
            setIsResending(false);
        }
    };

    const handleGoToVerification = () => {
        // Store the email for verification page
        if (resendEmail) {
            localStorage.setItem('pendingVerification', JSON.stringify({ 
                email: resendEmail,
                fromLogin: true 
            }));
        }
        
        if (setCurrentPage) {
            // If we're in a single-page context (like LandingPage)
            setCurrentPage('verification');
        } else {
            // Navigate to register page which will show verification component
            navigate('/register', { 
                state: { 
                    showVerification: true, 
                    email: resendEmail 
                } 
            });
        }
    };

    const handleBackClick = () => {
        if (onBack) {
            onBack();
        } else if (setCurrentPage) {
            setCurrentPage('main');
        } else {
            navigate('/');
        }
    };

    const handleRegisterClick = (e) => {
        e.preventDefault();
        if (setCurrentPage) {
            setCurrentPage('registry');
        } else {
            navigate('/register');
        }
    };

    const handleForgotPasswordClick = (e) => {
        e.preventDefault();
        navigate('/forgot-password', {
            state: {
                email: formData.email || ''
            }
        });
    };

    return (
        <div className={styles.loginContainer} dir="rtl">
            {/* Loading Overlay */}
            {isLoading && (
                <div className={styles.loadingOverlay}>
                    <div className={styles.loadingSpinner}>
                        <Loader2 className={styles.spinnerIcon} />
                        <p className={styles.loadingText}>{t('connectingToSystem')}</p>
                    </div>
                </div>
            )}

            {/* Back Button */}
            {showBackButton && (
                <div className={styles.backButtonContainer}>
                    <button
                        onClick={handleBackClick}
                        className={styles.backToHomeButton}
                        type="button"
                        disabled={isLoading}
                    >
                        <ArrowLeft size={20} />
                        {t('backToMain')}
                    </button>
                </div>
            )}

            <div className={styles.loginCardWrapper}>
                <div className={styles.loginCard}>
                    {/* Subscription Expired Alert */}
                    {subscriptionExpired && (
                        <div className={`${styles.messageContainer} ${styles.warning}`}>
                            <CreditCard size={20} />
                            <div className={styles.messageContent}>
                                <p className={styles.message}>{t('trialExpiredMessageShort')}</p>
                                <p className={styles.subMessage}>{t('choosePaymentPlan')}</p>
                                <button
                                    onClick={handleGoToPayment}
                                    className={styles.paymentButton}
                                    disabled={isLoading}
                                >
                                    <CreditCard size={16} />
                                    {t('goToPayment')}
                                </button>
                            </div>
                        </div>
                    )}

                    {error && !subscriptionExpired && (
                        <div className={`${styles.messageContainer} ${styles.error}`}>
                            <AlertCircle size={20} />
                            <p className={styles.message}>{error}</p>
                        </div>
                    )}

                    {success && (
                        <div className={`${styles.messageContainer} ${styles.success}`}>
                            <CheckCircle size={20} />
                            <p className={styles.message}>{success}</p>
                        </div>
                    )}

                    {resendSuccess && (
                        <div className={`${styles.messageContainer} ${styles.success}`}>
                            <CheckCircle size={20} />
                            <p className={styles.message}>{resendSuccess}</p>
                        </div>
                    )}

                    {showResendVerification && (
                        <div className={styles.verificationMessage}>
                            <div className={styles.verificationPrimary}>
                                {t('emailVerificationRequired2')}
                            </div>
                            <p>{t('emailVerificationRequired')}</p>
                            <div className={styles.verificationActions}>
                                <button
                                    onClick={handleResendVerification}
                                    className={styles.resendButton}
                                    disabled={isResending}
                                >
                                    <RefreshCw
                                        size={16}
                                        className={isResending ? styles.spinning : ''}
                                    />
                                    {isResending ? 'שולח...' : 'שלח קוד אימות חדש'}
                                </button>
                                <button
                                    onClick={handleGoToVerification}
                                    className={`${styles.resendButton} ${styles.secondary}`}
                                    disabled={isResending}
                                >
                                    מעבר לדף אימות
                                </button>
                            </div>
                            <p className={styles.verificationSecondary}>
                                כתובת המייל: {resendEmail}
                            </p>
                        </div>
                    )}

                    <div className={styles.formContainer}>
                        <div className={styles.header}>
                            <div className={styles.logoContainer}>
                                <User className={styles.logoIcon} />
                            </div>
                            <h1 className={styles.appTitle}>{t('systemLogin')}</h1>
                            <p className={styles.appSubtitle}>{t('enterLoginDetails')}</p>
                        </div>

                        <form onSubmit={handleSubmit}>
                            <div className={styles.formGroup}>
                                <label htmlFor="email" className={styles.formLabel}>{t('emailLabel')}</label>
                                <div className={styles.inputWrapper}>
                                    <input
                                        type="email"
                                        id="email"
                                        name="email"
                                        className={styles.formInput}
                                        placeholder="name@example.com"
                                        value={formData.email}
                                        onChange={handleChange}
                                        disabled={isLoading}
                                        autoComplete="email"
                                        required
                                    />
                                    <div className={styles.inputIconWrapper}>
                                        <Mail className={styles.inputIcon} />
                                    </div>
                                </div>
                            </div>

                            <div className={styles.formGroup}>
                                <div className={styles.labelRow}>
                                    <label htmlFor="password" className={styles.formLabel}>{t('passwordLabel')}</label>
                                    <button
                                        type="button"
                                        className={styles.forgotPassword}
                                        onClick={handleForgotPasswordClick}
                                        disabled={isLoading}
                                    >
                                        {t('forgotPassword')}
                                    </button>
                                </div>
                                <div className={styles.inputWrapper}>
                                    <input
                                        type={showPassword ? "text" : "password"}
                                        id="password"
                                        name="password"
                                        className={styles.formInput}
                                        placeholder="********"
                                        value={formData.password}
                                        onChange={handleChange}
                                        disabled={isLoading}
                                        autoComplete="current-password"
                                        required
                                    />
                                    <div className={styles.inputIconWrapper}>
                                        <Lock className={styles.inputIcon} />
                                    </div>
                                    <button
                                        type="button"
                                        className={styles.passwordToggle}
                                        onClick={() => setShowPassword(!showPassword)}
                                        disabled={isLoading}
                                        tabIndex={-1}
                                    >
                                        {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                                    </button>
                                </div>
                            </div>

                            {/* Remember Me Checkbox */}
                            <div className={styles.formGroup}>
                                <div className={styles.checkboxWrapper}>
                                    <input
                                        type="checkbox"
                                        id="rememberMe"
                                        name="rememberMe"
                                        className={styles.checkbox}
                                        checked={formData.rememberMe}
                                        onChange={handleChange}
                                        disabled={isLoading}
                                    />
                                    <label htmlFor="rememberMe" className={styles.checkboxLabel}>
                                        {t('rememberMe')}
                                    </label>
                                </div>
                            </div>

                            <button
                                type="submit"
                                className={`${styles.loginButton} ${isLoading ? styles.loading : ''}`}
                                disabled={isLoading}
                            >
                                {isLoading ? (
                                    <div className={styles.buttonContent}>
                                        <Loader2 className={styles.buttonSpinner} />
                                        {t('loggingIn')}
                                    </div>
                                ) : (
                                    t('loginButton')
                                )}
                            </button>
                        </form>
                    </div>

                    <div className={styles.registerContainer}>
                        <p className={styles.registerText}>
                            אין לך חשבון? <button
                                type="button"
                                onClick={handleRegisterClick}
                                className={styles.link}
                                disabled={isLoading}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    padding: 0,
                                    cursor: isLoading ? 'not-allowed' : 'pointer'
                                }}
                            >
                                הירשם עכשיו
                            </button>
                        </p>
                    </div>
                </div>

                <div className={styles.footer}>
                    <p className={styles.copyright}>© 2025 ניהול ועד בית. כל הזכויות שמורות.</p>
                </div>
            </div>
        </div>
    );
}